/**
 * @file 미사용 자동 로그아웃 (전 환경 3시간. session.ts IDLE_LOGOUT_MS)
 * @description 마지막 활동(탭, iframe 공유) 후
 *              - 로그아웃 5분 전: "5분 후 자동 로그아웃" 경고 + 카운트다운 + 계속 사용
 *              - 미사용 시간 지남: 서버 세션 폐기 → 재로그인 모달 (화면은 그대로)
 *              시각 비교라서 백그라운드 탭, 절전 후에도 정확하다
 */
import { useEffect, useState, useRef, useSyncExternalStore } from 'react'
import { useIntl, IntlShape } from 'react-intl'
import { Dialog, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import oc from 'open-color'
import useStayOnTop, { STAY_ON_TOP_CLASS } from './useStayOnTop'
import {
    getHasMe,
    subscribeMe,
    getRelogin,
    subscribeRelogin,
    getLastActivity,
    markActivity,
    touchSessionIfNeeded,
    openRelogin,
    broadcastIdleLogout,
    gqlFetch,
    isTopWindow,
    IDLE_LOGOUT_MS,
    IDLE_LOGOUT_MINUTES,
    IDLE_WARN_MS,
    IDLE_WARN_Z_INDEX
} from 'ts-utils/session'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel']

// 활동 기록 (모든 창, iframe 에서). 경고/로그아웃은 최상위 창에서만
export function useActivityTracker() {
    const me = useSyncExternalStore(subscribeMe, getHasMe)

    useEffect(() => {
        if (!me) return

        let last = 0

        const handleActivity = () => {
            const now = Date.now()
            if (now - last < 1000) return
            last = now

            // 이미 미사용 시간이 지났으면 움직여도 되살리지 않는다 (로그아웃 처리 대기)
            if (now - getLastActivity() >= IDLE_LOGOUT_MS) return

            markActivity()
            touchSessionIfNeeded()
        }

        ACTIVITY_EVENTS.forEach(c => window.addEventListener(c, handleActivity, { passive: true, capture: true }))

        return () => {
            ACTIVITY_EVENTS.forEach(c => window.removeEventListener(c, handleActivity, { capture: true } as any))
        }
    }, [me])
}

// 안내 문구의 미사용 시간 (3시간)
export const idleTimeText = (f: IntlShape['formatMessage']) => (
    IDLE_LOGOUT_MINUTES % 60 === 0
        ? f({ id: 'component.ReLogin.idle.hour' }, { n: IDLE_LOGOUT_MINUTES / 60 })
        : f({ id: 'component.ReLogin.idle.minute' }, { n: IDLE_LOGOUT_MINUTES })
)

const pad = (n: number) => String(n).padStart(2, '0')

const WARN_SECONDS = (IDLE_LOGOUT_MS - IDLE_WARN_MS) / 1000

// 재로그인 모달(ReLoginModal)과 같은 모양
const useStyles = makeStyles(theme => ({
    paper: {
        borderRadius: 16,
        padding: '36px 32px 28px',
        textAlign: 'center',
        [theme.breakpoints.down('xs')]: {
            margin: 16,
            padding: '28px 20px 20px'
        }
    },
    icon: {
        width: 52,
        height: 52,
        margin: '0 auto 16px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: oc.orange[0],
        color: oc.orange[7]
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: oc.gray[9],
        wordBreak: 'keep-all'
    },
    desc: {
        marginTop: 10,
        fontSize: 14,
        lineHeight: 1.6,
        color: oc.gray[7],
        wordBreak: 'keep-all'
    },
    count: {
        marginTop: 24,
        fontSize: 40,
        fontWeight: 700,
        letterSpacing: 2,
        fontVariantNumeric: 'tabular-nums',
        color: oc.gray[9]
    },
    bar: {
        height: 6,
        margin: '12px 0 24px',
        borderRadius: 3,
        overflow: 'hidden',
        background: oc.gray[2],
        '& > div': {
            height: '100%',
            borderRadius: 3,
            background: oc.orange[6],
            transition: 'width 1s linear'
        }
    },
    button: {
        height: 48,
        borderRadius: 8,
        fontSize: 15,
        fontWeight: 600
    }
}))

function IdleWatcher() {
    const { formatMessage: f } = useIntl()

    const classes = useStyles()

    const me = useSyncExternalStore(subscribeMe, getHasMe)
    const relogin = useSyncExternalStore(subscribeRelogin, getRelogin)

    const [ remain, setRemain ] = useState<number | null>(null)

    // 다른 다이얼로그가 나중에 열려도 경고창이 맨 위(포커스)
    const stayKey = useStayOnTop(remain !== null)

    const originTitle = useRef<string | null>(null)

    useEffect(() => {
        if (!me || relogin.open || !isTopWindow()) {
            setRemain(null)
            return
        }

        // 앱 시작(새로고침)도 활동으로 본다
        markActivity()
        touchSessionIfNeeded()

        let loggingOut = false

        const tick = () => {
            const idle = Date.now() - getLastActivity()

            if (idle >= IDLE_LOGOUT_MS) {
                if (loggingOut) return
                loggingOut = true
                setRemain(null)

                // 서버 세션 폐기 (같은 브라우저 다른 탭도 같이 풀린다) → 모든 탭 재로그인 모달
                gqlFetch(`mutation Logout { logout }`).catch(() => {})
                broadcastIdleLogout()
                openRelogin('IDLE')
                return
            }

            if (idle >= IDLE_WARN_MS) setRemain(Math.ceil((IDLE_LOGOUT_MS - idle) / 1000))
            else setRemain(null)
        }

        tick()
        const timer = window.setInterval(tick, 1000)

        return () => window.clearInterval(timer)
    }, [me, relogin.open])

    // 다른 탭에 있어도 알수 있게 탭 제목 변경
    useEffect(() => {
        if (remain !== null) {
            if (originTitle.current === null) originTitle.current = document.title
            document.title = f({ id: 'component.ReLogin.idle.tabTitle' })
        } else if (originTitle.current !== null) {
            document.title = originTitle.current
            originTitle.current = null
        }
    }, [remain, f])

    const handleContinue = () => {
        markActivity()
        touchSessionIfNeeded(true)
        setRemain(null)
    }

    if (remain === null) return null

    return (
        <Dialog key={stayKey} open={true} maxWidth="xs" fullWidth onClose={handleContinue} style={{ zIndex: IDLE_WARN_Z_INDEX }} className={STAY_ON_TOP_CLASS} classes={{ paper: classes.paper }}>
            <div className={classes.icon}>
                <AccessTimeIcon/>
            </div>
            <Typography className={classes.title}>{f({ id: 'component.ReLogin.idle.title' })}</Typography>
            <Typography className={classes.desc}>{f({ id: 'component.ReLogin.idle.desc' }, { time: idleTimeText(f) })}</Typography>
            <div className={classes.count}>{`${pad(Math.floor(remain / 60))}:${pad(remain % 60)}`}</div>
            <div className={classes.bar}>
                <div style={{ width: `${Math.max(0, Math.min(100, (remain / WARN_SECONDS) * 100))}%` }}/>
            </div>
            <Button variant="contained" color="primary" disableElevation fullWidth onClick={handleContinue} className={classes.button}>
                {f({ id: 'component.ReLogin.idle.continue' })}
            </Button>
        </Dialog>
    )
}

export default IdleWatcher
